/**
 * Direct slide master extraction for individual slides
 * Used when processing slides one at a time in live presentations
 */

import { layoutId as normalizeLayoutId, masterId as normalizeMasterId, cleanId as cleanIdUtil } from '../utils/idNormalizer';

export interface SlideMasterInfo {
  masterId: string;  // Normalized with master_ prefix
  masterName: string;
  layoutId: string;  // Normalized with layout_ prefix
  layoutName: string;
}

// REMOVED: getMasterInfoForCurrentSlide - no callers (getMasterInfoInContext is used instead)

/**
 * Get master information within an existing context
 * This version reuses the existing PowerPoint context to avoid context isolation
 */
export async function getMasterInfoInContext(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide
): Promise<SlideMasterInfo | undefined> {
  try {
    // Load the slide's layout
    const layout = slide.layout;
    layout.load("id,name");
    
    // Load all masters to find which one owns this layout
    const masters = context.presentation.slideMasters;
    // First load the items collection and direct properties
    masters.load("items/id,items/name,items/layouts");
    
    await context.sync();
    
    // Now load the nested layout items
    for (let i = 0; i < masters.items.length; i++) {
      masters.items[i].layouts.load("items/id");
    }
    
    await context.sync();
    
    // Use clean IDs for comparison (without hash suffix)
    const rawLayoutId = cleanIdUtil((layout as any).id || "");
    const layoutName = layout.name || "Unknown Layout";
    
    // Now check each master - everything is loaded
    for (let i = 0; i < masters.items.length; i++) {
      const master = masters.items[i];
      
      // Check if this master contains our layout
      for (let j = 0; j < master.layouts.items.length; j++) {
        const rawMasterLayoutId = cleanIdUtil((master.layouts.items[j] as any).id || "");
        
        if (rawMasterLayoutId === rawLayoutId) {
          // Found the master that owns this layout
          // Return normalized IDs with proper prefixes
          return {
            masterId: normalizeMasterId((master as any).id || ""),
            masterName: master.name || `Master ${i + 1}`,
            layoutId: normalizeLayoutId(rawLayoutId),
            layoutName: layoutName
          };
        }
      }
    }
    
    console.warn(`Could not find master for layout ${rawLayoutId}`);
    return undefined;
    
  } catch (error) {
    console.error("Error getting master info in context:", error);
    return undefined;
  }
}

// cleanId function is now imported from idNormalizer

